import React from 'react';
import './SevenDayFlowTrend.css';
import { MultiDayMarketActivityResponse } from '../../api/metrics';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SevenDayFlowTrendProps {
  data: MultiDayMarketActivityResponse | undefined;
  isLoading: boolean;
}

const SevenDayFlowTrend: React.FC<SevenDayFlowTrendProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="seven-day-trend loading">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading 7-day trend data...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.dailySummaries || data.dailySummaries.length === 0) {
    return (
      <div className="seven-day-trend empty">
        <p>No trend data available</p>
      </div>
    );
  }

  const { dailySummaries } = data;

  // Prepare chart data
  const labels = dailySummaries.map(day => {
    // Parse date string safely to avoid timezone issues
    // day.date format: "2025-10-15"
    const [year, month, dayOfMonth] = day.date.split('-').map(Number);
    const date = new Date(year, month - 1, dayOfMonth); // month is 0-indexed
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  const buyData = dailySummaries.map(day => day.totalBuyVolume);
  const sellData = dailySummaries.map(day => day.totalSellVolume);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Buys',
        data: buyData,
        borderColor: 'rgb(230, 57, 70)',
        backgroundColor: 'rgba(230, 57, 70, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 5,
        pointHoverRadius: 8,
        borderWidth: 3,
      },
      {
        label: 'Sells',
        data: sellData,
        borderColor: 'rgb(6, 167, 125)',
        backgroundColor: 'rgba(6, 167, 125, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 5,
        pointHoverRadius: 8,
        borderWidth: 3,
      }
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#1e293b',
          font: {
            size: 14,
            family: "'Rajdhani', sans-serif",
            weight: 600,
          },
          usePointStyle: true,
          padding: 20,
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#e2e8f0',
        bodyColor: '#e2e8f0',
        borderColor: '#475569',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          family: "'Rajdhani', sans-serif",
          weight: 600,
        },
        bodyFont: {
          size: 13,
          family: "'Rajdhani', sans-serif",
        },
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
          drawBorder: false,
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12,
            family: "'Rajdhani', sans-serif",
            weight: 500,
          },
          padding: 8,
        }
      },
      y: {
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
          drawBorder: false,
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12,
            family: "'Rajdhani', sans-serif",
            weight: 500,
          },
          padding: 8,
          callback: function(value: any) {
            if (value >= 1000000) {
              return '$' + (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return '$' + (value / 1000).toFixed(0) + 'k';
            }
            return '$' + value;
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  return (
    <div className="seven-day-trend">
      <div className="chart-container">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default SevenDayFlowTrend;
